import { useState, useCallback } from 'react';

import {
  ClientFileUploadUrlResponse,
  useGetClientFileUploadUrlMutation,
} from '../generated/graphql';
import { useHotelUnsavedChanges } from '../hotel/session';
import { useHotelWorkflow } from '../hotel/useHotelWorkflow';
import { popToast } from '../utilities/popToast';

export interface StagedFile {
  id: string;
  file: File;
  displayFilename: string;
  tempFilePath: string;
  originalFilename: string;
  fileType: string;
}

export interface UseStagedFileUploadResult {
  stagedFiles: StagedFile[];
  isUploading: boolean;
  stageFile: (file: File, displayName?: string) => Promise<StagedFile | null>;
  removeFile: (id: string) => void;
  clearFiles: () => void;
}

export function useStagedFileUpload(): UseStagedFileUploadResult {
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  useHotelUnsavedChanges(stagedFiles.length > 0);
  const [isUploading, setIsUploading] = useState(false);
  const beginWorkflow = useHotelWorkflow();

  const [getFileUploadUrl] = useGetClientFileUploadUrlMutation();

  const stageFile = useCallback(
    async (file: File, displayName?: string): Promise<StagedFile | null> => {
      setIsUploading(true);
      const workflow = beginWorkflow();

      try {
        // Step 1: Get signed upload URL for temp storage
        const { data: urlData } = await getFileUploadUrl({
          variables: {
            input: {
              filename: file.name,
              contentType: file.type,
            },
          },
        });

        workflow.assertActive();
        const uploadUrlResponse =
          urlData?.getClientFileUploadUrl as ClientFileUploadUrlResponse;

        if (!uploadUrlResponse?.url) {
          throw new Error('Failed to get upload URL');
        }

        // Step 2: Upload file to temp storage
        const uploadResponse = await fetch(uploadUrlResponse.url, {
          method: 'PUT',
          signal: workflow.signal,
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        });

        workflow.assertActive();
        if (!uploadResponse.ok) {
          throw new Error('Failed to upload file');
        }

        // Step 3: Create staged file record
        const stagedFile: StagedFile = {
          id: crypto.randomUUID(),
          file,
          displayFilename: displayName || file.name,
          tempFilePath: uploadUrlResponse.path,
          originalFilename: file.name,
          fileType: file.type,
        };

        setStagedFiles((prev) => [...prev, stagedFile]);

        return stagedFile;
      } catch (error) {
        if (workflow.signal.aborted) return null;
        popToast(
          `Error uploading file: ${error instanceof Error ? error.message : 'Unknown error'}`,
          { type: 'error' },
        );
        return null;
      } finally {
        workflow.finish();
        if (!workflow.signal.aborted) setIsUploading(false);
      }
    },
    [getFileUploadUrl, beginWorkflow],
  );

  const removeFile = useCallback((id: string) => {
    setStagedFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const clearFiles = useCallback(() => {
    setStagedFiles([]);
  }, []);

  return {
    stagedFiles,
    isUploading,
    stageFile,
    removeFile,
    clearFiles,
  };
}
